import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { useToast } from '@/hooks/ui/use-toast'

/**
 * Lands here after Better Auth completes the Google OAuth exchange server-side.
 * The session cookie is already set and the domain `accounts` row is provisioned
 * by a server hook, so this page only needs to route the user onward.
 *
 * TODO(onboarding): the pre-cutover callback also initialised a new user's
 * language from the browser (`detectBrowserLanguage` + `updateLanguage`) and
 * routed first-time users (null `last_daily_brief_sent_at`) to `/success`.
 * Both were dropped in the Better Auth cutover and are not yet restored — new
 * users default to `'en'` and skip onboarding. Re-add when onboarding is
 * reinstated (move language detection into a one-time post-login effect).
 */
export const AuthCallback = () => {
  const navigate = useNavigate()
  const { toast } = useToast()

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const errorParam = urlParams.get('error')
    const flow = urlParams.get('flow') === 'link' ? 'link' : 'default'

    // A user who declines the Google consent screen gets `access_denied`; the
    // login page shows a softer "you cancelled" message for that code.
    if (errorParam === 'access_denied') {
      navigate('/login?error=oauth_cancelled', { replace: true })
      return
    }

    if (errorParam) {
      toast({
        title: 'Authorization failed',
        description: 'We could not connect your account. Please try again.',
        variant: 'destructive',
      })
      navigate('/login?error=oauth_failed', { replace: true })
      return
    }

    if (flow === 'link') {
      navigate('/settings?connected=1', { replace: true })
      return
    }

    navigate('/', { replace: true })
  }, [navigate, toast])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
        <h2 className="mb-2 font-semibold text-2xl text-foreground">Processing Google Authorization</h2>
        <p className="text-[15px] text-muted-foreground">Completing your sign-in...</p>
      </div>
    </div>
  )
}
